import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";
import {
  Phone,
  MapPin,
  Mail,
  ChevronDown,
  CheckCircle,
  Star,
  Users,
  Baby,
  AlertCircle,
  CalendarDays,
  MoonStar,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

// ─── EmailJS config ────────────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID       = "service_zstl8e4";
const EMAILJS_TEMPLATE_ID      = "template_b1lvq2a";  // email către agenție
const EMAILJS_AUTOREPLY_ID     = "template_3vbkjzn";  // auto-reply către client
const EMAILJS_PUBLIC_KEY       = "6qpENhkEYXGihbrme";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Destination {
  id: string;
  name: string;
  country: string;
  tagline: string;
  image: string;
  flag: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────
const DESTINATIONS: Destination[] = [
  {
    id: "Egipt",
    name: "Egipt",
    country: "Egypt",
    tagline: "Piramidele și misterele antice",
    image: "https://images.unsplash.com/photo-1705628080778-f86b2f90a114?w=800&q=80",
    flag: "🇪🇬",
  },
  {
    id: "Turcia",
    name: "Turcia",
    country: "Turkey",
    tagline: "Istanbul & Riviera Turcă",
    image: "https://images.unsplash.com/photo-1774850691859-0e339f94bebb?w=800&q=80",
    flag: "🇹🇷",
  },
  {
    id: "Grecia",
    name: "Grecia",
    country: "Greece",
    tagline: "Santorini & insulele Egeene",
    image: "https://images.unsplash.com/photo-1506242592132-b7476b527d50?w=800&q=80",
    flag: "🇬🇷",
  },
  {
    id: "Zanzibar",
    name: "Zanzibar",
    country: "Zanzibar",
    tagline: "Paradisul din Oceanul Indian",
    image: "https://images.unsplash.com/photo-1559934303-856aae38610f?w=800&q=80",
    flag: "🇹🇿",
  },
  {
    id: "Spania",
    name: "Spania",
    country: "Spain",
    tagline: "Barcelona & Costa del Sol",
    image: "https://images.unsplash.com/photo-1758471206484-0eaa2568320c?w=800&q=80",
    flag: "🇪🇸",
  },
  {
    id: "Muntenegru",
    name: "Muntenegru",
    country: "Montenegro",
    tagline: "Golful Kotor & Marea Adriatică",
    image: "https://images.unsplash.com/photo-1689517686262-628708bbc5d7?w=800&q=80",
    flag: "🇲🇪",
  },
];

// ─── Form Schema ──────────────────────────────────────────────────────────────
const formSchema = z.object({
  nume:         z.string().min(2, "Numele trebuie să aibă cel puțin 2 caractere"),
  prenume:      z.string().min(2, "Prenumele trebuie să aibă cel puțin 2 caractere"),
  telefon:      z.string().min(7, "Nr. de telefon invalid").regex(/^[+\d\s\-()]{7,20}$/, "Nr. de telefon invalid"),
  email:        z.string().email("Adresă de email invalidă"),
  destinatie:   z.string().min(1, "Selectați o destinație"),
  adulti:       z.coerce.number().min(1, "Minim 1 adult"),
  copii:        z.coerce.number().min(0, "Număr invalid"),
  varsta_copii:   z.string().optional(),
  data_plecare:   z.string().min(1, "Selectați data de plecare"),
  nopti:          z.coerce.number().min(1, "Minim 1 noapte").max(60, "Maxim 60 nopți"),
  mesaj:          z.string().optional(),
});

type FormSchema = z.infer<typeof formSchema>;

// ─── Scroll helper ────────────────────────────────────────────────────────────
const scrollTo = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

// ─── Input class helper ───────────────────────────────────────────────────────
const inputCls =
  "w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:border-transparent transition-all text-sm";
const focusGold = { "--tw-ring-color": "#BF9B57" } as React.CSSProperties;

// ─── Component ────────────────────────────────────────────────────────────────
export default function HomePage() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedDest, setSelectedDest] = useState<string>("");
  const [hasCopii, setHasCopii]         = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: { adulti: 2, copii: 0 },
  });

  const copiiValue = watch("copii");

  // ── Send via EmailJS ────────────────────────────────────────────────────────
  const onSubmit = async (data: FormSchema) => {
    setStatus("sending");
    setErrorMsg("");

    const destLabel =
      DESTINATIONS.find((d) => d.id === data.destinatie)?.name ?? data.destinatie;

    const templateParams: Record<string, string> = {
      to_email:      "avitur88@gmail.com",
      from_name:     `${data.nume} ${data.prenume}`,
      from_email:    data.email,
      reply_to:      data.email,
      telefon:       data.telefon,
      destinatie:    destLabel,
      adulti:        String(data.adulti),
      copii:         String(data.copii),
      varsta_copii:  data.varsta_copii || "—",
      data_plecare:  data.data_plecare,
      nopti:         String(data.nopti),
      mesaj:         data.mesaj || "—",
    };

    try {
      // 1️⃣ Email principal către agenție
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      // 2️⃣ Auto-reply către client
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_AUTOREPLY_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      setStatus("success");
      reset();
      setSelectedDest("");
      setHasCopii(false);
    } catch (err) {
      console.error("EmailJS error:", err);
      setStatus("error");
      setErrorMsg(
        "A apărut o eroare la trimitere. Vă rugăm să ne contactați telefonic sau să încercați din nou."
      );
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">

      {/* ─── HEADER ─────────────────────────────────────────────── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b"
        style={{ background: "rgba(13,31,45,0.96)", borderColor: "rgba(191,155,87,0.25)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Premium Style Travel"
              className="h-12 w-auto object-contain drop-shadow-md"
            />
          </div>
          <nav className="hidden md:flex items-center gap-6">
            {(["destinatii", "formular", "contact"] as const).map((sec) => (
              <button
                key={sec}
                onClick={() => scrollTo(sec)}
                className="text-white/80 hover:text-white text-sm uppercase tracking-widest transition-colors duration-200"
                style={{ ["--hover-color" as string]: "#BF9B57" }}
              >
                {sec === "destinatii" ? "Destinații" : sec === "formular" ? "Solicită Ofertă" : "Contact"}
              </button>
            ))}
          </nav>
          <a
            href="tel:+37360609292"
            className="flex items-center gap-2 text-sm font-medium transition-colors"
            style={{ color: "#BF9B57" }}
          >
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline font-mono">+373 60609292</span>
          </a>
        </div>
      </header>

      {/* ─── HERO ───────────────────────────────────────────────── */}
      <section
        id="hero"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ background: "#0d1f2d" }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1765288116151-27dd3eb25938?w=1920&q=80')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d1f2d]/80 via-[#0d1f2d]/60 to-[#0d1f2d]/90" />
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#BF9B57] to-transparent" />

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6"
            style={{ background: "rgba(191,155,87,0.15)", border: "1px solid rgba(191,155,87,0.35)" }}
          >
            <Star className="w-3.5 h-3.5" style={{ color: "#BF9B57" }} />
            <span className="text-xs uppercase tracking-widest font-medium" style={{ color: "#BF9B57" }}>
              Agenție Turistică Premium
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Descoperă lumea cu{" "}
            <span style={{ color: "#BF9B57" }}>Premium Style</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-white/75 text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Vacanțe de vis în cele mai frumoase destinații ale lumii. Lasă-ne să
            creăm pentru tine o experiență de neuitat.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={() => scrollTo("formular")}
              className="px-8 py-3.5 rounded-full font-semibold text-sm uppercase tracking-widest transition-all duration-200"
              style={{ background: "#BF9B57", color: "#fff", boxShadow: "0 4px 20px -4px rgba(191,155,87,0.5)" }}
            >
              Solicită Ofertă Gratuită
            </button>
            <button
              onClick={() => scrollTo("destinatii")}
              className="px-8 py-3.5 rounded-full font-semibold text-sm uppercase tracking-widest transition-all duration-200 border"
              style={{ background: "rgba(35,75,110,0.4)", color: "#fff", borderColor: "rgba(35,75,110,0.8)" }}
            >
              Vezi Destinații
            </button>
          </motion.div>
        </div>

        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer"
          onClick={() => scrollTo("destinatii")}
        >
          <ChevronDown className="w-7 h-7 text-white/50" />
        </motion.div>
      </section>

      {/* ─── DESTINATIONS ───────────────────────────────────────── */}
      <section id="destinatii" className="py-24 px-4" style={{ background: "#0d1f2d" }}>
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-xs uppercase tracking-widest mb-3 font-medium" style={{ color: "#BF9B57" }}>
              Destinații Exclusive
            </p>
            <h2
              className="text-4xl sm:text-5xl font-bold text-white mb-4"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              Alege destinația visurilor tale
            </h2>
            <p className="text-white/50 max-w-xl mx-auto">
              Selectează o destinație pentru a completa formularul de solicitare
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {DESTINATIONS.map((dest, i) => (
              <motion.div
                key={dest.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -6, scale: 1.02 }}
                onClick={() => {
                  setSelectedDest(dest.id);
                  setValue("destinatie", dest.id);
                  scrollTo("formular");
                }}
                className="relative group rounded-2xl overflow-hidden cursor-pointer border transition-all duration-300"
                style={{
                  borderColor: selectedDest === dest.id ? "#BF9B57" : "rgba(191,155,87,0.1)",
                  boxShadow: "0 8px 30px -6px rgba(0,0,0,0.5)",
                }}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={dest.image}
                    alt={dest.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div
                    className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ background: "linear-gradient(to right, transparent, #BF9B57, transparent)" }}
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl">{dest.flag}</span>
                    <h3
                      className="text-2xl font-bold text-white"
                      style={{ fontFamily: "var(--font-heading)" }}
                    >
                      {dest.name}
                    </h3>
                  </div>
                  <p className="text-white/65 text-sm mb-3">{dest.tagline}</p>
                  <div
                    className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-widest"
                    style={{ color: "#BF9B57" }}
                  >
                    <span>Solicită Ofertă</span>
                    <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FORM ───────────────────────────────────────────────── */}
      <section id="formular" className="py-24 px-4 bg-background">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <p className="text-xs uppercase tracking-widest mb-3 font-medium" style={{ color: "#BF9B57" }}>
              Ofertă Personalizată
            </p>
            <h2
              className="text-4xl sm:text-5xl font-bold text-foreground mb-4"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              Solicită oferta ta
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Completează formularul de mai jos și îți vom trimite o ofertă
              personalizată direct pe email.
            </p>
          </motion.div>

          <AnimatePresence mode="wait">
            {/* ── SUCCESS ── */}
            {status === "success" ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-16 px-8 rounded-3xl border"
                style={{ borderColor: "rgba(34,197,94,0.3)", background: "rgba(34,197,94,0.05)" }}
              >
                <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: "#22c55e" }} />
                <h3
                  className="text-3xl font-bold text-foreground mb-3"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  Cerere trimisă cu succes!
                </h3>
                <p className="text-muted-foreground text-lg mb-6">
                  Vă mulțumim! Veți fi contactat în cel mai scurt timp.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="px-6 py-2.5 rounded-full text-sm font-semibold text-white transition-colors"
                  style={{ background: "#BF9B57" }}
                >
                  Trimite o nouă cerere
                </button>
              </motion.div>
            ) : (
              /* ── FORM ── */
              <motion.form
                key="form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                onSubmit={handleSubmit(onSubmit)}
                className="bg-card border border-border rounded-3xl p-8 sm:p-10 shadow-xl"
              >

                {/* Error banner */}
                <AnimatePresence>
                  {status === "error" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="flex items-start gap-3 p-4 rounded-xl mb-6 border border-destructive/30 bg-destructive/5 overflow-hidden"
                    >
                      <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                      <p className="text-sm text-destructive">{errorMsg}</p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Row 1 – Nume + Prenume */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Nume <span className="text-destructive">*</span>
                    </label>
                    <input
                      {...register("nume")}
                      placeholder="ex: Popescu"
                      style={focusGold}
                      className={inputCls}
                    />
                    {errors.nume && <p className="text-destructive text-xs mt-1">{errors.nume.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Prenume <span className="text-destructive">*</span>
                    </label>
                    <input
                      {...register("prenume")}
                      placeholder="ex: Ion"
                      style={focusGold}
                      className={inputCls}
                    />
                    {errors.prenume && <p className="text-destructive text-xs mt-1">{errors.prenume.message}</p>}
                  </div>
                </div>

                {/* Row 2 – Telefon + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Nr. de telefon <span className="text-destructive">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        {...register("telefon")}
                        placeholder="+373 XXX XXX XX"
                        style={focusGold}
                        className={`${inputCls} pl-10 font-mono`}
                      />
                    </div>
                    {errors.telefon && <p className="text-destructive text-xs mt-1">{errors.telefon.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Adresă email <span className="text-destructive">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        {...register("email")}
                        type="email"
                        placeholder="ex: ion@gmail.com"
                        style={focusGold}
                        className={`${inputCls} pl-10`}
                      />
                    </div>
                    {errors.email && <p className="text-destructive text-xs mt-1">{errors.email.message}</p>}
                  </div>
                </div>

                {/* Row 3 – Destinație */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Destinație <span className="text-destructive">*</span>
                  </label>
                  <div className="relative">
                    <select
                      {...register("destinatie")}
                      value={selectedDest}
                      onChange={(e) => {
                        setSelectedDest(e.target.value);
                        setValue("destinatie", e.target.value);
                      }}
                      style={focusGold}
                      className={`${inputCls} appearance-none cursor-pointer`}
                    >
                      <option value="">— Selectați destinația —</option>
                      {DESTINATIONS.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.flag} {d.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                  </div>
                  {errors.destinatie && <p className="text-destructive text-xs mt-1">{errors.destinatie.message}</p>}
                </div>

                {/* Row 4 – Adulți + Copii */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      <span className="flex items-center gap-1.5">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        Nr. adulți <span className="text-destructive">*</span>
                      </span>
                    </label>
                    <input
                      {...register("adulti")}
                      type="number"
                      min={1}
                      max={20}
                      style={focusGold}
                      className={inputCls}
                    />
                    {errors.adulti && <p className="text-destructive text-xs mt-1">{errors.adulti.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      <span className="flex items-center gap-1.5">
                        <Baby className="w-4 h-4 text-muted-foreground" />
                        Nr. copii
                      </span>
                    </label>
                    <input
                      {...register("copii")}
                      type="number"
                      min={0}
                      max={10}
                      style={focusGold}
                      className={inputCls}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setValue("copii", val);
                        setHasCopii(val > 0);
                      }}
                    />
                  </div>
                </div>

                {/* Row 5 – Vârsta copii (condiționat) */}
                <AnimatePresence>
                  {(hasCopii || (copiiValue && copiiValue > 0)) && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mb-5 overflow-hidden"
                    >
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        Vârsta copiilor{" "}
                        <span className="text-muted-foreground font-normal">(ex: 3, 7 ani)</span>
                      </label>
                      <input
                        {...register("varsta_copii")}
                        placeholder="Indicați vârsta fiecărui copil separată prin virgulă"
                        style={focusGold}
                        className={inputCls}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Row 6 – Data plecare + Nopți */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      <span className="flex items-center gap-1.5">
                        <CalendarDays className="w-4 h-4 text-muted-foreground" />
                        Data de plecare <span className="text-destructive">*</span>
                      </span>
                    </label>
                    <input
                      {...register("data_plecare")}
                      type="date"
                      min={new Date().toISOString().split("T")[0]}
                      style={focusGold}
                      className={inputCls}
                    />
                    {errors.data_plecare && <p className="text-destructive text-xs mt-1">{errors.data_plecare.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      <span className="flex items-center gap-1.5">
                        <MoonStar className="w-4 h-4 text-muted-foreground" />
                        Nr. nopți <span className="text-destructive">*</span>
                      </span>
                    </label>
                    <input
                      {...register("nopti")}
                      type="number"
                      min={1}
                      max={60}
                      placeholder="ex: 7"
                      style={focusGold}
                      className={inputCls}
                    />
                    {errors.nopti && <p className="text-destructive text-xs mt-1">{errors.nopti.message}</p>}
                  </div>
                </div>

                {/* Row 7 – Mesaj */}
                <div className="mb-7">
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Cerințe speciale / Mesaj
                  </label>
                  <textarea
                    {...register("mesaj")}
                    rows={3}
                    placeholder="Descrieți preferințele, întrebările sau cerințele speciale..."
                    style={focusGold}
                    className={`${inputCls} resize-none`}
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="w-full py-4 rounded-full font-semibold text-sm uppercase tracking-widest disabled:opacity-60 transition-all duration-200"
                  style={{
                    background: "#234B6E",
                    color: "#ffffff",
                    boxShadow: "0 4px 20px -4px rgba(35,75,110,0.5)",
                  }}
                >
                  {status === "sending" ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Se trimite...
                    </span>
                  ) : (
                    "Trimite Cererea →"
                  )}
                </button>

                <p className="text-center text-muted-foreground text-xs mt-4">
                  Datele dvs. sunt confidențiale. Vă vom contacta în maxim 24 ore.
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ─── WHY US ─────────────────────────────────────────────── */}
      <section className="py-20 px-4" style={{ background: "#0d1f2d" }}>
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2
              className="text-4xl font-bold text-white"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              De ce să ne alegeți?
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: "✈️", title: "Experiență & Profesionalism", desc: "Echipa noastră vă oferă consultanță personalizată și pachete adaptate nevoilor fiecărui client." },
              { icon: "💎", title: "Calitate Premium", desc: "Selectăm cele mai bune hoteluri și servicii pentru o experiență de vacanță impecabilă." },
              { icon: "🤝", title: "Suport Complet", desc: "Vă însoțim pas cu pas, de la rezervare până la întoarcerea acasă, cu asistență 24/7." },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-6 rounded-2xl border border-white/10 hover:border-[#BF9B57]/40 transition-colors"
              >
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3
                  className="text-xl font-bold text-white mb-3"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  {item.title}
                </h3>
                <p className="text-white/55 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CONTACT ────────────────────────────────────────────── */}
      <section id="contact" className="py-20 px-4 bg-background">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-4xl font-bold text-foreground mb-4"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              Contactează-ne
            </h2>
            <p className="text-muted-foreground">
              Suntem aici pentru a răspunde la orice întrebare.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: <Phone className="w-5 h-5" />, label: "Telefon",  value: "+373 60609292",       href: "tel:+37360609292" },
              { icon: <Mail  className="w-5 h-5" />, label: "Email",    value: "avitur88@gmail.com",  href: "mailto:avitur88@gmail.com" },
              { icon: <MapPin className="w-5 h-5" />, label: "Adresă",  value: "Str. Călărași 45, Chișinău", href: "https://maps.google.com/?q=Str+Calarasi+45+Chisinau" },
            ].map((item, i) => (
              <motion.a
                key={i}
                href={item.href}
                target={item.href.startsWith("http") ? "_blank" : undefined}
                rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="flex flex-col items-center text-center p-6 rounded-2xl border border-border bg-card transition-all duration-200 cursor-pointer hover:border-[#BF9B57]/50"
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                  style={{ background: "rgba(191,155,87,0.15)", color: "#BF9B57" }}
                >
                  {item.icon}
                </div>
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">
                  {item.label}
                </p>
                <p className="font-semibold text-foreground font-mono text-sm">{item.value}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─────────────────────────────────────────────── */}
      <footer
        className="py-10 px-4 border-t border-white/10 text-center"
        style={{ background: "#080f18" }}
      >
        <div className="flex items-center justify-center gap-2 mb-4">
          <img
            src="/logo.png"
            alt="Premium Style Travel"
            className="h-14 w-auto object-contain opacity-90"
          />
        </div>
        <p className="text-white/35 text-xs">
          © {new Date().getFullYear()} Premium Style Travel · Str. Călărași 45, Chișinău · +373
          60609292
        </p>
        <p className="text-white/20 text-xs mt-1">Toate drepturile rezervate.</p>
      </footer>
    </div>
  );
}
